import React, { useEffect, useState } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, Container, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap"
import { get } from "helpers/api_helper"
import Breadcrumbs from "components/Common/Breadcrumb"
import useLogisticsOperation from "pages/EventsManagement/components/Tabs/Logistics/useLogisticsOperation"
import { actorName, plural } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import { changeLabel, distributionLabel, emptyLabel, estimatesLink, formatDetails, formatTitle, momentLabel, reasonLabel, sourceLabel, triggerName,
  valueLabel } from "./estimateLabels"
import "pages/EventsManagement/components/Tabs/Logistics/logistics.scss"
import "./estimates.scss"

const UNCONFIRMED = "The recalculation could not be confirmed. Reload the page in a minute to see whether it ran."
const messages = {
  ERROR_PERMISSIONS: "Only an active SuperAdmin can recalculate the estimates.",
  SUPER_ADMIN_REQUIRED: "Only an active SuperAdmin can recalculate the estimates.",
  OPERATION_PAYLOAD_CONFLICT: "This request belongs to a different change. Reload before trying again.",
}
const recalculationMessage = code => messages[code] || UNCONFIRMED
const RECALCULATED = "Estimates recalculated. The run is listed below with your name."
const ORGANIZER_ID = /^[a-f\d]{24}$/i
// A run lists this many of its changes until all are asked for.
const SHOWN_CHANGES = 5

// The two tables of estimates, each with its relaxation rule explained once under its title (US 48).
const tables = {
  places: {
    title: "Qualification places",
    rule: "Most frequent result on similar finished competitions (groups, group size, game type, category) · last 24 months · ties go to more "
      + "places. Organizer first, with at least 3 similar; otherwise global; with fewer than 3, one criterion is dropped at a time: category, then "
      + "game type, then group size, never the groups. Fewer than 3 even then: the smallest power of 2 at least the number of groups, to check.",
    format: "Format", value: "Estimate", valueClass: "", distribution: true,
  },
  occupancy: {
    title: "Occupancy",
    rule: "Average of the final Main List ÷ capacity on similar finished competitions (category, game type, level) · last 24 months · used by "
      + "the consumption forecast. Organizer first, with at least 3 similar; otherwise global; with fewer than 3, level is dropped, then game "
      + "type. Fewer than 3 even then: the full capacity, to check. Competitions without a confirmed participant are left out.",
    format: "Similar competitions", value: "Average occupancy", valueClass: "text-right", distribution: false,
  },
}

// A section of the page: its title and note, then its rows, a spinner while the scope loads, or what it lacks.
const Section = ({ title, note, ready, empty, children }) => <Card tag="section" aria-label={title}><CardBody>
  <h4 className="card-title mb-1">{title}</h4>
  <p className="text-muted small">{note}</p>
  {!ready ? <div className="text-center my-3"><Spinner color="primary" size="sm" /></div> : children || <p className="text-muted mb-0">{empty}</p>}
</CardBody></Card>

// One row per similar format: the estimate a sheet of that format reads, and where it comes from.
const EstimatesTable = ({ kind, rows }) => {
  const table = tables[kind]
  return <Table size="sm" className="logistics-stack mb-0">
    <thead><tr>
      <th>{table.format}</th><th className={table.valueClass}>{table.value}</th>{table.distribution && <th>Distribution</th>}
      <th className="text-right">Competitions</th><th>Source</th>
    </tr></thead>
    <tbody>{rows.map(row => <tr key={Object.values(row.format).join("|")}>
      <td><strong>{formatTitle(kind, row.format)}</strong>
        {formatDetails(kind, row.format) && <div className="small text-muted">{formatDetails(kind, row.format)}</div>}</td>
      <td className={table.valueClass} data-label={table.value}>
        {valueLabel(kind, row.value)}{row.level === "fixed" && <Badge className="badge-soft-warning ml-1">to check</Badge>}
      </td>
      {table.distribution && <td data-label="Distribution">{distributionLabel(row.distribution)}</td>}
      <td className="text-right" data-label="Competitions">{row.competitions}</td>
      <td data-label="Source">{sourceLabel(kind, row)}{reasonLabel(row) && <div className="small text-muted">{reasonLabel(row)}</div>}</td>
    </tr>)}</tbody>
  </Table>
}

const RunChanges = ({ run }) => {
  const [all, setAll] = useState(false)
  const shown = all ? run.changes : run.changes.slice(0, SHOWN_CHANGES)
  const outside = run.changed - run.changes.length
  return <td data-label="Changed estimates">
    {shown.length ? <ul className="list-unstyled mb-0">
      {shown.map(change => <li key={[change.kind, change.level, ...Object.values(change.format)].join("|")}>{changeLabel(change)}</li>)}
    </ul> : <span className="text-muted">{outside ? "No change in this view" : "No change"}</span>}
    {!all && run.changes.length > SHOWN_CHANGES && <Button color="link" size="sm" className="p-0" onClick={() => setAll(true)}>
      {`Show all ${run.changes.length}`}
    </Button>}
    {outside > 0 && <div className="small text-muted">{`${outside} more outside this view`}</div>}
  </td>
}

// Logistics → Estimates (US 46–48, ticket 22 Q8): the qualification places and the occupancy the
// sheets read, global or for one organizer, where they come from, and the runs that recalculated
// them. The sheet's "Details" link opens the page on the event's organizer (estimatesLink).
export default function LogisticsEstimates() {
  const location = useLocation()
  const history = useHistory()
  const requested = new URLSearchParams(location.search).get("organizer")
  const organizerId = requested && ORGANIZER_ID.test(requested) ? requested.toLowerCase() : null
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState({ loading: true })
  const [confirming, setConfirming] = useState(false)
  const [failure, setFailure] = useState("")
  const [notice, setNotice] = useState("")
  const operation = useLogisticsOperation(undefined, recalculationMessage)

  // Another scope keeps the page's header while its tables load.
  useEffect(() => {
    let current = true
    setState(previous => ({ view: previous.view, loading: true }))
    get(`/admin/logistics/estimates${organizerId ? `?organizerId=${organizerId}` : ""}`)
      .then(response => current && setState({ view: response?.data }))
      .catch(() => current && setState(previous => ({ view: previous.view, error: "The estimates could not be loaded." })))
    return () => { current = false }
  }, [organizerId, attempt])

  const { view } = state
  const status = view?.status
  const organizers = view?.organizers || []
  // The tables show only the scope the address asks for, never the previous one while it loads.
  const ready = Boolean(view) && (view.organizer?.id ?? null) === organizerId
  const organizerName = organizers.find(({ id }) => id === organizerId)?.name || (ready && view.organizer?.name) || null
  const scope = { calculated: Boolean(status), organizerId, organizerName }
  const showScope = id => history.replace(estimatesLink(id))

  const close = () => {
    if (operation.pending) return
    setConfirming(false)
    setFailure("")
  }
  // A result that cannot be confirmed reloads the page, which shows the run once it is saved.
  const recalculate = async () => {
    setFailure("")
    const result = await operation.run("recalculateEstimates", "/admin/logistics/estimates/recalculate")
    if (!result || result.status === "cancelled") return
    if (result.status === "saved") {
      setConfirming(false)
      setNotice(RECALCULATED)
      setAttempt(value => value + 1)
      return
    }
    if (result.status !== "error") setAttempt(value => value + 1)
    setFailure(result.message || UNCONFIRMED)
  }

  return <div className="page-content logistics-estimates">
    <Container fluid>
      <Breadcrumbs title="Logistics" breadcrumbItem="Estimates" path="/logistics/estimates" />
      {notice && <Alert color="success" isOpen toggle={() => setNotice("")}>{notice}</Alert>}
      {state.error && <Alert color="danger">{state.error}{" "}
        <Button color="link" className="p-0 align-baseline" onClick={() => setAttempt(value => value + 1)}>Retry</Button>
      </Alert>}
      {!view && state.loading && <div className="text-center my-4"><Spinner color="primary" /></div>}
      {view && <>
        <Card tag="section" aria-label="Estimates status"><CardBody className="logistics-estimates-head">
          <div className="logistics-estimates-facts">
            <div>
              <div className="small text-muted">Last calculation</div>
              {status?.calculatedAt ? <><strong>{momentLabel(status.calculatedAt)}</strong>{status.trigger && ` · ${triggerName(status.trigger).toLowerCase()}`}</>
                : <strong>Not calculated yet</strong>}
            </div>
            <div>
              <div className="small text-muted">Last check</div>
              <strong>{status?.checkedAt ? momentLabel(status.checkedAt) : "—"}</strong>
            </div>
            <div>
              <div className="small text-muted" id="logistics-estimates-scope">Scope</div>
              <ButtonGroup aria-labelledby="logistics-estimates-scope">
                <Button size="sm" color="primary" outline={Boolean(organizerId)} aria-pressed={!organizerId} onClick={() => showScope(null)}>Global</Button>
                <Button size="sm" color="primary" outline={!organizerId} aria-pressed={Boolean(organizerId)} disabled={!organizerId && !organizers.length}
                  onClick={() => !organizerId && showScope(organizers[0].id)}>Organizer</Button>
              </ButtonGroup>
            </div>
            {organizerId && <div className="logistics-estimates-organizer">
              <Label for="logistics-estimates-organizer" className="small text-muted mb-0">Organizer</Label>
              <Input id="logistics-estimates-organizer" type="select" bsSize="sm" value={organizerId} onChange={event => showScope(event.target.value)}>
                {!organizers.some(({ id }) => id === organizerId) && <option value={organizerId}>
                  {organizerName ? `${organizerName} (no history)` : "Organizer without history"}
                </option>}
                {organizers.map(({ id, name }) => <option key={id} value={id}>{name || "Unnamed organizer"}</option>)}
              </Input>
            </div>}
          </div>
          <Button color="primary" size="sm" onClick={() => setConfirming(true)} disabled={operation.pending}>
            <i className="bx bx-sync mr-1" aria-hidden="true" />Recalculate now
          </Button>
        </CardBody></Card>

        {["places", "occupancy"].map(kind => <Section key={kind} title={tables[kind].title} note={tables[kind].rule} ready={ready}
          empty={emptyLabel(kind, scope)}>
          {ready && view[kind].length > 0 && <EstimatesTable kind={kind} rows={view[kind]} />}
        </Section>)}

        <Section title="Runs that calculated something" ready={ready} empty="No run has calculated anything yet."
          note="Checks that found nothing new only update “Last check”. Each run lists the estimates of this view it changed.">
          {ready && view.runs.length > 0 && <Table size="sm" className="logistics-stack mb-0">
            <thead><tr><th>Moment</th><th>Trigger · actor</th><th className="text-right">Analysed</th><th>Changed estimates</th></tr></thead>
            <tbody>{view.runs.map(run => <tr key={run.id}>
              <td><strong>{momentLabel(run.createdAt)}</strong></td>
              <td data-label="Trigger · actor">{triggerName(run.trigger)}<div className="small text-muted">{actorName(run.actor)}</div></td>
              <td className="text-right" data-label="Analysed">{run.analysed ? <>
                {plural(run.analysed.occupancy, "competition")}
                <div className="small text-muted">{`${plural(run.analysed.events, "event")} · ${run.analysed.places} in groups`}</div>
              </> : "—"}</td>
              <RunChanges run={run} />
            </tr>)}</tbody>
          </Table>}
        </Section>
      </>}
    </Container>

    <Modal isOpen={confirming} toggle={close} centered>
      <ModalHeader toggle={close}>Recalculate estimates now</ModalHeader>
      <ModalBody>
        <p className="mb-0">Runs the same pass as the nightly check: qualification places, occupancy and last year’s calculated consumption, global and for
          every organizer. It is logged with your name.</p>
        {failure && <Alert color="danger" className="mt-3 mb-0">{failure}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={close} disabled={operation.pending}>Cancel</Button>
        <Button color="primary" onClick={recalculate} disabled={operation.pending}>{operation.pending ? "Recalculating…" : "Recalculate"}</Button>
      </ModalFooter>
    </Modal>
  </div>
}
